import React, {FC, useEffect, useState} from 'react'
import axios from 'axios';
import DialogContent from '@pixel/core/DialogContent'
import DialogActions from '@pixel/core/DialogActions'
import { useForm, FormProvider } from 'react-hook-form'
import { LocalizedText } from '../../contexts/Localization/LocalizationTypes'
import LocalizedButton from '../../components/LocalizedButton'
import {useMutation} from "@tanstack/react-query";
import {Alert, Snackbar} from "@mui/material";
import {useMultipleLocalizedText} from "../../contexts/Localization/localizationHooks";
import FieldListsForm from "./FieldListsForm";
import {FieldListSearchResult} from "../types/FieldList";

interface FieldListFormContainerProps {
    selectedFieldList: FieldListSearchResult
    onModalClose: () => void
    fetchData: () => void
}

const FieldListFormContainer: FC<FieldListFormContainerProps> = ({
    selectedFieldList,
    onModalClose,
    fetchData
}) => {
    const {
        FIELD_LIST_FORM_ALERT_MESSAGE_SUCCESS,
        FIELD_LIST_FORM_ALERT_MESSAGE_ERROR,
        FIELD_LIST_EDIT_ALERT_MESSAGE_SUCCESS,
        FIELD_LIST_EDIT_ALERT_MESSAGE_ERROR
    } = useMultipleLocalizedText()

    const [formSubmitted, setFormSubmitted] = useState(false)
    const [formResponse, setFormResponse] = useState(null)
    const [formMessage, setFormMessage] = useState('')

    const form = useForm({
        defaultValues: {
            name: '',
            fieldListValues: [],
            value: '',
        },
    })

    const CREATE_NEW_FIELD_LIST_ENDPOINT = '/admin/action/automation/admin/saveFieldList';

    const submitData = async (data: any) => {
        delete data.value;
        let doFetchData = true;
        try {
            const response = await axios.post(CREATE_NEW_FIELD_LIST_ENDPOINT, data);
            if (response.status === 201 || response.status === 200) {
                if (response.data.validationError) {
                    setFormResponse('error');
                    if (response.data.message) {
                        setFormMessage(response.data.message);
                    } else {
                        setFormMessage(FIELD_LIST_EDIT_ALERT_MESSAGE_ERROR);

                    }
                    doFetchData = false;
                }

                else {
                    setFormResponse('success');
                    if (selectedFieldList?.fieldListId) {
                        setFormMessage(FIELD_LIST_EDIT_ALERT_MESSAGE_SUCCESS);
                    } else {
                        setFormMessage(FIELD_LIST_FORM_ALERT_MESSAGE_SUCCESS);
                    }
                }

            } else {
                doFetchData = false;
                setFormResponse('error');
                if (selectedFieldList?.fieldListId) {
                    setFormMessage(FIELD_LIST_EDIT_ALERT_MESSAGE_ERROR);
                }
                setFormMessage(FIELD_LIST_FORM_ALERT_MESSAGE_ERROR);
            }
        } catch (e) {
            setFormResponse('error');
            setFormMessage(FIELD_LIST_FORM_ALERT_MESSAGE_ERROR);
        }

        setFormSubmitted(true);
        if (doFetchData) {
            fetchData();
        }
    }

    const mutation = useMutation(submitData);

    const onSubmit = (data: any) => {
        mutation.mutate(data);
    }

    useEffect(() => {
        if (Boolean(selectedFieldList)) {
            form.reset(selectedFieldList)
        }
    }, [selectedFieldList])


    return (
        <FormProvider {...form}>
            <Snackbar open={formSubmitted} autoHideDuration={1000} onClose={() => {
                setFormSubmitted(false)
                if (formResponse == 'success') {
                    onModalClose()
                }
            }}>
                <Alert onClose={() => setFormSubmitted(false)} severity={formResponse} sx={{ width: '100%' }}>
                    {formMessage}
                </Alert>
            </Snackbar>
            <DialogContent sx={{ padding: 0 }}>
                <FieldListsForm />
            </DialogContent>
            <DialogActions>
                <LocalizedButton
                    id={`field-list-form-cancel-button`}
                    text={LocalizedText.Cancel}
                    variant="outlined"
                    color="secondary"
                    onClick={onModalClose}
                />
                <LocalizedButton
                    id={`field-list-form-save-button`}
                    text={LocalizedText.AUTOMATION_FORM_SAVE}
                    variant="contained"
                    color="primary"
                    onClick={form.handleSubmit(onSubmit)}
                />
            </DialogActions>
        </FormProvider>
    )
}

export default FieldListFormContainer
